import React, { useEffect, useMemo, useState } from "react";
import {
  BriefcaseBusiness,
  CheckCircle2,
  Clock3,
  Plus,
  Video,
  Scissors,
  Palette,
  Share2,
  Layers,
  Calendar,
  User,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DateTimePicker } from "@/components/ui/date-time-picker";

const DEPARTMENTS = [
  { key: "all", label: "All Departments", icon: Layers },
  { key: "shooting", label: "Shooting", icon: Video, color: "text-sky-600 bg-sky-50 border-sky-200" },
  { key: "editing", label: "Editing", icon: Scissors, color: "text-violet-600 bg-violet-50 border-violet-200" },
  { key: "graphic_designer", label: "Graphic Designer", icon: Palette, color: "text-blue-600 bg-blue-50 border-blue-200" },
  { key: "social_media_manager", label: "Social Media Manager", icon: Share2, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
];

const emptyForm = {
  client_id: "",
  department: "shooting",
  work_type: "",
  description: "",
  assigned_to: "",
  due_date: "",
};

const labels = {
  todo: "To do",
  in_progress: "In progress",
  submitted: "Submitted",
  approved: "Approved",
  revision: "Revision",
};

export default function WorkPage() {
  const { user } = useAuth();
  const manager = ["owner", "pillar"].includes(user?.role);
  const [activities, setActivities] = useState([]);
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedDept, setSelectedDept] = useState("all");
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const requests = [apiClient.get("/work-activities")];
      if (manager) requests.push(apiClient.get("/clients"), apiClient.get("/users"));
      const results = await Promise.all(requests);
      setActivities(results[0].data);
      if (manager) {
        setClients(results[1].data);
        setMembers(results[2].data.filter((member) => !["owner", "pillar"].includes(member.role)));
      }
    } catch {
      toast.error("Could not load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (activity, status) => {
    try {
      await apiClient.patch(`/work-activities/${activity.id}`, { status });
      toast.success("Task status updated");
      load();
    } catch {
      toast.error("Could not update task");
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    try {
      await apiClient.post("/work-activities", {
        ...form,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      });
      toast.success("Task created and assigned");
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not assign task");
    }
  };

  // Filter tasks by selected department
  const filteredActivities = useMemo(() => {
    if (selectedDept === "all") return activities;
    return activities.filter((a) => {
      if (selectedDept === "social_media_manager") {
        return a.department === "social_media" || a.department === "social_media_manager";
      }
      if (selectedDept === "graphic_designer") {
        return a.department === "graphic_design" || a.department === "graphic_designer";
      }
      return a.department === selectedDept;
    });
  }, [activities, selectedDept]);

  const grouped = useMemo(
    () => ({
      active: filteredActivities.filter((a) => ["todo", "in_progress", "revision"].includes(a.status)),
      submitted: filteredActivities.filter((a) => a.status === "submitted"),
      approved: filteredActivities.filter((a) => a.status === "approved"),
    }),
    [filteredActivities]
  );

  return (
    <div className="space-y-6" data-testid="task-management-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Task Management</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Department-wise workflows for Shooting, Editing, Graphic Design, and Social Media
          </p>
        </div>
        {manager && (
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="mr-2 h-4 w-4" />
            Assign New Task
          </Button>
        )}
      </div>

      {/* Department Tabs Filter */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {DEPARTMENTS.map((dept) => {
          const Icon = dept.icon;
          const isSelected = selectedDept === dept.key;
          return (
            <button
              key={dept.key}
              onClick={() => setSelectedDept(dept.key)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold border transition-all whitespace-nowrap ${
                isSelected
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {dept.label}
            </button>
          );
        })}
      </div>

      {/* Assignment Form */}
      {manager && showForm && (
        <Card className="p-5 border border-border">
          <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs font-semibold mb-1 block">Client *</label>
              <select
                required
                className="w-full h-10 rounded-md border border-input bg-background p-2 text-sm"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              >
                <option value="">Select client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold mb-1 block">Department Workflow *</label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background p-2 text-sm"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
              >
                <option value="shooting">Shooting Department</option>
                <option value="editing">Editing Department</option>
                <option value="graphic_designer">Graphic Designer Department</option>
                <option value="social_media_manager">Social Media Manager Department</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold mb-1 block">Assign To *</label>
              <select
                required
                className="w-full h-10 rounded-md border border-input bg-background p-2 text-sm"
                value={form.assigned_to}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
              >
                <option value="">Select team member</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.role})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold mb-1 block">Task Title / Type *</label>
              <input
                required
                className="w-full h-10 rounded-md border border-input bg-background p-2 text-sm"
                placeholder="e.g. Raw Footage B-Roll Ingest or Instagram Grid Layout"
                value={form.work_type}
                onChange={(e) => setForm({ ...form, work_type: e.target.value })}
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-semibold mb-1 block">Due Date & Time</label>
              <DateTimePicker
                value={form.due_date}
                onChange={(val) => setForm({ ...form, due_date: val })}
                placeholder="Set task deadline"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-semibold mb-1 block">Task Brief & Instructions</label>
              <textarea
                className="w-full rounded-md border border-input bg-background p-2 text-sm outline-none focus:ring-1 focus:ring-primary"
                rows="2"
                placeholder="Details, deliverable links, aspect ratio requirements..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="flex gap-2 md:col-span-2">
              <Button type="submit">Create Task Assignment</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Task Columns / Stages */}
      {loading ? (
        <p className="text-muted-foreground text-sm">Loading task workflows...</p>
      ) : filteredActivities.length === 0 ? (
        <Card className="p-10 text-center">
          <BriefcaseBusiness className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">No tasks found for this department flow.</p>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {[
            ["In Progress & Todo", grouped.active, "border-amber-200 bg-amber-500"],
            ["Awaiting Approval", grouped.submitted, "border-violet-200 bg-violet-500"],
            ["Approved & Done", grouped.approved, "border-emerald-200 bg-emerald-500"],
          ].map(([heading, items, badgeColor]) => (
            <section key={heading} className="space-y-3">
              <div className="flex items-center justify-between pb-1">
                <h2 className="font-semibold text-sm flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${badgeColor}`} />
                  {heading}
                </h2>
                <span className="text-xs font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-full border">
                  {items.length}
                </span>
              </div>

              <div className="space-y-3">
                {items.length === 0 ? (
                  <div className="py-8 text-center border border-dashed rounded-xl text-muted-foreground text-xs">
                    No tasks in this stage
                  </div>
                ) : (
                  items.map((a) => {
                    const deptMeta = DEPARTMENTS.find((d) => d.key === a.department) || DEPARTMENTS[1];
                    const DeptIcon = deptMeta.icon;
                    return (
                      <Card key={a.id} className="p-4 space-y-3 hover-lift border border-border">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold text-sm">{a.work_type}</p>
                            <p className="text-xs text-muted-foreground">{a.client_name}</p>
                          </div>
                          <Badge variant="outline" className="text-[10px]">
                            {labels[a.status] || a.status}
                          </Badge>
                        </div>

                        {/* Department Badge */}
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded border">
                            <DeptIcon className="w-3 h-3 text-primary" />
                            {deptMeta.label}
                          </span>
                        </div>

                        {a.description && (
                          <p className="text-xs text-foreground/80 leading-relaxed bg-muted/30 p-2 rounded">
                            {a.description}
                          </p>
                        )}

                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border/60">
                          {manager ? (
                            <span className="flex items-center gap-1 truncate">
                              <User className="w-3 h-3" /> {a.assigned_name || "Unassigned"}
                            </span>
                          ) : (
                            <span />
                          )}
                          {a.due_date && (
                            <span className="flex items-center gap-1 text-[11px]">
                              <Clock3 className="w-3 h-3" />
                              {new Date(a.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        {/* Workflow Action Buttons */}
                        <div className="flex flex-wrap gap-2 pt-1">
                          {!manager && a.status === "todo" && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => update(a, "in_progress")}>
                              <Clock3 className="mr-1 h-3 w-3" />
                              Start Task
                            </Button>
                          )}
                          {!manager && ["in_progress", "revision"].includes(a.status) && (
                            <Button size="sm" className="h-7 text-xs" onClick={() => update(a, "submitted")}>
                              <CheckCircle2 className="mr-1 h-3 w-3" />
                              Submit for Approval
                            </Button>
                          )}
                          {manager && a.status === "submitted" && (
                            <>
                              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => update(a, "approved")}>
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => update(a, "revision")}>
                                Request Revision
                              </Button>
                            </>
                          )}
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
